<template>
  <div class="page-stack">
    <section class="section-shell section-shell--hero">
      <div class="section-head">
        <div>
          <div class="section-kicker">Etsy</div>
          <h3 class="section-title">店铺运费模板</h3>
          <p class="section-subtitle">直接读取 Etsy 店铺里的 shipping profiles，产品页会复用这里的模板做绑定。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button class="btn btn-secondary" @click="loadProfiles">刷新运费模板</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Profiles</div>
          <h3 class="section-title">官方运费模板列表</h3>
        </div>
        <div class="text-sm text-slate-400">共 {{ profiles.length }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>模板</th>
              <th>基础费用</th>
              <th>处理时间</th>
              <th>目的地</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in profiles" :key="item.shippingProfileId">
              <td class="min-w-[280px]">
                <div class="font-semibold text-slate-900">{{ item.title || `Profile #${item.shippingProfileId}` }}</div>
                <div class="mt-2 text-sm text-slate-500">ID：{{ item.shippingProfileId }}</div>
                <div class="mt-2 text-sm text-slate-500">Origin：{{ item.originCountryIso || '-' }}</div>
              </td>
              <td class="min-w-[220px] text-sm text-slate-600">
                <div>Primary：{{ formatMoney(item.primaryCost) }}</div>
                <div class="mt-2">Secondary：{{ formatMoney(item.secondaryCost) }}</div>
              </td>
              <td class="min-w-[220px] text-sm text-slate-600">
                <div>{{ formatProcessing(item) }}</div>
              </td>
              <td class="min-w-[320px] text-sm text-slate-600">
                <div v-if="item.destinations?.length" class="space-y-2">
                  <div v-for="destination in item.destinations" :key="destination.destinationId || `${destination.destinationCountryIso}-${destination.destinationRegion}`">
                    {{ destination.destinationCountryIso || destination.destinationRegion || '默认目的地' }}
                    / {{ formatMoney(destination.primaryCost) }}
                    <span v-if="destination.secondaryCost !== null && destination.secondaryCost !== undefined">
                      / {{ formatMoney(destination.secondaryCost) }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  {{ item.destinationCountryIso || item.destinationRegion || '-' }}
                </div>
              </td>
              <td>
                <button class="btn btn-secondary" @click="openDetail(item)">明细</button>
              </td>
            </tr>
            <tr v-if="!profiles.length">
              <td colspan="5" class="table-empty">当前没有读取到 Etsy 运费模板</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="detailVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--lg" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">运费模板明细</h4>
            <p class="mt-2 text-sm text-slate-500">这里保留 Etsy 原始返回，方便你和店铺后台逐项对照。</p>
          </div>
          <button class="btn btn-secondary" @click="closeDetail">关闭</button>
        </div>

        <div v-if="detail" class="modal-panel__body mt-6">
          <div class="grid gap-4 md:grid-cols-2">
            <div class="settings-card">
              <div class="field-label">Profile ID</div>
              <div class="text-sm text-slate-700">{{ detail.shippingProfileId || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">Title</div>
              <div class="text-sm text-slate-700">{{ detail.title || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">Origin</div>
              <div class="text-sm text-slate-700">{{ detail.originCountryIso || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">Processing</div>
              <div class="text-sm text-slate-700">{{ formatProcessing(detail) }}</div>
            </div>
          </div>

          <div class="mt-4">
            <label class="field-label">原始返回</label>
            <pre class="code-panel">{{ prettyRawData }}</pre>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-secondary" @click="closeDetail">关闭</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { etsyApi } from '@/api';
import { notifyError } from '@/utils/feedback';

const profiles = ref([]);
const detail = ref(null);
const detailVisible = ref(false);

const prettyRawData = computed(() => JSON.stringify(detail.value?.rawData || {}, null, 2));

function formatMoney(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value} USD`;
}

function formatProcessing(item) {
  const min = item?.minProcessingTime;
  const max = item?.maxProcessingTime;
  const unit = item?.processingTimeUnit || 'days';
  if (min === null || min === undefined || max === null || max === undefined) {
    return '-';
  }
  return `${min}-${max} ${unit}`;
}

async function loadProfiles() {
  try {
    const res = await etsyApi.listShippingProfiles();
    profiles.value = res.data || [];
  } catch (error) {
    notifyError(error.message || '读取 Etsy 运费模板失败');
  }
}

function openDetail(item) {
  detail.value = item;
  detailVisible.value = true;
}

function closeDetail() {
  detailVisible.value = false;
  detail.value = null;
}

onMounted(loadProfiles);
</script>
